import { useMemo } from 'react';
import { transformChoicesToOptions } from '../components/Learner/ReviewPage/ReviewComponents/shared/reviewUtils';

/**
 * Custom hook to normalize user answer IDs for MultipleCorrectAnswer
 */
export function useNormalizedUserIds(userAnswer, questionChoices) {
  return useMemo(() => {
    let ids = [];
    
    if (!userAnswer) {
      return [];
    }
    
    if (Array.isArray(userAnswer)) {
      if (userAnswer.length === 0) {
        return [];
      }
      
      if (typeof userAnswer[0] === 'object' && userAnswer[0] !== null) {
        ids = userAnswer.map(item => {
          if (item.idx !== undefined) return Number(item.idx);
          if (item.id !== undefined) return Number(item.id);
          return null;
        }).filter(id => id !== null && id !== undefined);
      } else if (typeof userAnswer[0] === 'string') {
        // User answer is array of strings (text) - need to match with choices to get idx
        const choices = questionChoices || [];
        ids = userAnswer.map(text => {
          // Try exact match first
          let matchedChoice = choices.find(choice => {
            const choiceText = choice['option-text'] || choice.text || '';
            return choiceText === text || choiceText.trim() === text.trim();
          });
          
          // If no exact match, try case-insensitive and trimmed
          if (!matchedChoice) {
            matchedChoice = choices.find(choice => {
              const choiceText = (choice['option-text'] || choice.text || '').trim().toLowerCase();
              const userText = (text || '').trim().toLowerCase();
              return choiceText === userText;
            });
          }
          
          if (matchedChoice) {
            const idx = matchedChoice.idx !== undefined ? matchedChoice.idx : matchedChoice.id;
            return typeof idx === 'number' ? idx : Number(idx);
          }
          
          // DEBUG: Log if no match found
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ useNormalizedUserIds - No match found for text:', text, 'in choices:', choices);
          }
          
          return null;
        }).filter(id => id !== null && id !== undefined);
      } else {
        ids = userAnswer.map(id => {
          if (typeof id === 'number') return id;
          const numId = Number(id);
          return !isNaN(numId) ? numId : null;
        }).filter(id => id !== null && id !== undefined);
      }
    } else if (typeof userAnswer === 'object' && userAnswer !== null) {
      if (userAnswer.idx !== undefined) {
        ids = [Number(userAnswer.idx)];
      } else if (userAnswer.id !== undefined) {
        ids = [Number(userAnswer.id)];
      }
    } else {
      const numId = typeof userAnswer === 'number' ? userAnswer : Number(userAnswer);
      if (!isNaN(numId)) {
        ids = [numId];
      }
    }
    
    return ids.map(id => {
      if (typeof id === 'number') return id;
      const numId = Number(id);
      return !isNaN(numId) ? numId : null;
    }).filter(id => id !== null && id !== undefined);
  }, [userAnswer, questionChoices]);
}

/**
 * Custom hook to normalize correct answer IDs for MultipleCorrectAnswer
 */
export function useNormalizedCorrectIds(correctAnswer) {
  return useMemo(() => {
    let ids = [];
    
    if (Array.isArray(correctAnswer)) {
      if (correctAnswer.length > 0 && typeof correctAnswer[0] === 'object' && correctAnswer[0] !== null) {
        ids = correctAnswer.map(item => {
          if (item.idx !== undefined) return Number(item.idx);
          if (item.id !== undefined) return Number(item.id);
          return null;
        }).filter(id => id !== null && id !== undefined);
      } else {
        ids = correctAnswer.map(id => {
          if (typeof id === 'number') return id;
          const numId = Number(id);
          return !isNaN(numId) ? numId : null;
        }).filter(id => id !== null && id !== undefined);
      }
    } else if (correctAnswer !== null && correctAnswer !== undefined) {
      if (typeof correctAnswer === 'object') {
        if (correctAnswer.idx !== undefined) {
          ids = [Number(correctAnswer.idx)];
        } else if (correctAnswer.id !== undefined) {
          ids = [Number(correctAnswer.id)];
        }
      } else {
        const numId = typeof correctAnswer === 'number' ? correctAnswer : Number(correctAnswer);
        if (!isNaN(numId)) {
          ids = [numId];
        }
      }
    }
    
    return ids.map(id => {
      if (typeof id === 'number') return id;
      const numId = Number(id);
      return !isNaN(numId) ? numId : null;
    }).filter(id => id !== null && id !== undefined);
  }, [correctAnswer]);
}

/**
 * Custom hook to transform MultipleCorrectAnswer question data
 */
export function useMultipleCorrectAnswerData(question) {
  return useMemo(() => {
    const options = transformChoicesToOptions(question.choices).map(opt => ({
      ...opt,
      id: typeof opt.id === 'number' ? opt.id : Number(opt.id),
    }));

    const questionText = question.text || question['text-soal'] || '';
    const formattedText = questionText.replace(/\n/g, '<br />');

    return {
      ...question,
      options: options,
      text: formattedText,
    };
  }, [question]);
}

